import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, MouseEvent, ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WidgetHeader } from './WidgetHeader';
import { WidgetToolbar } from './WidgetToolbar';
import { TickerLinkButton } from './TickerLinkButton';
import { useTickerLink } from '../contexts/TickerLinkContext';
import { profileService } from '../services/profileService';
import {
  fetchDealerGexHeatmap,
  formatDealerGex,
  formatMd,
  heatmapStatusCode,
  isEmptyHeatmap,
  parseHorizonDays,
  HEATMAP_HORIZON_DAYS,
  HEATMAP_HORIZON_OPTIONS,
  HEATMAP_STALE_MS,
  type DealerGexHeatmap,
  type HeatmapHorizon,
  type HeatmapGreek,
  type HeatmapSize,
  type HeatmapTerrain,
} from '../services/dealerGexHeatmapService';
import {
  getOptionsDisplayTicker,
  normalizeOptionsTicker,
} from '../utils/tickerDisplay';
import { drawHeatmap, type TerrainClock } from './gex-heatmap/drawHeatmap';
import { customerGamma } from './gex-heatmap/fieldRaster';
import { hoverCell } from './gex-heatmap/overlay';

interface ClockFrame {
  view: DealerGexHeatmap;
  field: number[][];
  clock: TerrainClock;
}

function lastPrint(path: (number | null)[]): number | null {
  for (let i = path.length - 1; i >= 0; i--) {
    const px = path[i];
    if (px != null && Number.isFinite(px)) return px;
  }
  return null;
}

/** Gamma view of the 0DTE clock. Charm and the session book stay on `data`. */
function clockFrame(data: DealerGexHeatmap, terrain: HeatmapTerrain): ClockFrame {
  return {
    field: customerGamma(terrain.z),
    clock: {
      times: terrain.times,
      plus2: terrain.plus2,
      plus1: terrain.plus1,
      minus1: terrain.minus1,
      minus2: terrain.minus2,
      path: terrain.path,
      asof: terrain.asof,
    },
    view: {
      ...data,
      dates: terrain.times,
      strikes: terrain.strikes,
      spot: data.spot,
    },
  };
}

interface DealerGexHeatmapWidgetProps {
  windowId: string;
  onRemove: () => void;
}

interface HeatmapSettings {
  ticker: string;
  size: HeatmapSize;
  horizonDays: HeatmapHorizon;
  greek: HeatmapGreek;
}

interface HoverTip {
  x: number;
  y: number;
  date: string;
  price: number;
  value: number;
}

const DEFAULT_SETTINGS: HeatmapSettings = {
  ticker: 'SPXW',
  size: 'vol_inf',
  horizonDays: HEATMAP_HORIZON_DAYS,
  greek: 'gamma',
};

export function DealerGexHeatmapWidget({
  windowId,
  onRemove,
}: DealerGexHeatmapWidgetProps): ReactElement {
  const [settings, setSettings] = useState<HeatmapSettings>(DEFAULT_SETTINGS);
  const [tickerInput, setTickerInput] = useState(
    getOptionsDisplayTicker(DEFAULT_SETTINGS.ticker)
  );
  const loadedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wellRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [tip, setTip] = useState<HoverTip | null>(null);

  useEffect(() => {
    setTip(null);
  }, [settings.greek, settings.size, settings.ticker, settings.horizonDays]);

  const { isWindowLinked } = useTickerLink();
  const isTickerLinked = isWindowLinked(windowId);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const response = await profileService.getFrontendState();
        const saved = response.frontend_state?.gexHeatmapWidgets?.[windowId] as
          | Partial<HeatmapSettings>
          | undefined;
        if (!cancelled && saved) {
          const ticker =
            typeof saved.ticker === 'string' && saved.ticker
              ? normalizeOptionsTicker(saved.ticker)
              : DEFAULT_SETTINGS.ticker;
          const size: HeatmapSize = saved.size === 'oi' ? 'oi' : 'vol_inf';
          const horizonDays = parseHorizonDays(saved.horizonDays);
          const greek: HeatmapGreek = saved.greek === 'charm' ? 'charm' : 'gamma';
          setSettings({ ticker, size, horizonDays, greek });
          setTickerInput(getOptionsDisplayTicker(ticker));
        }
      } catch {
        // Keep defaults.
      } finally {
        loadedRef.current = true;
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [windowId]);

  useEffect(() => {
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void profileService
        .saveFrontendState({
          gexHeatmapWidgets: { [windowId]: settings },
        })
        .catch(() => undefined);
    }, 1000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [windowId, settings]);

  useEffect(() => {
    const el = wellRef.current;
    if (!el) return;
    const measure = (): void => {
      setBox((prev) => {
        const width = el.clientWidth;
        const height = el.clientHeight;
        if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
          return prev;
        }
        return { width, height };
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleTickerSelect = useCallback((ticker: string): void => {
    const normalized = normalizeOptionsTicker(ticker.trim().toUpperCase());
    setSettings((prev) => ({ ...prev, ticker: normalized }));
    setTickerInput(getOptionsDisplayTicker(normalized));
  }, []);

  const handleTickerSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (tickerInput.trim()) handleTickerSelect(tickerInput.trim());
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['dealerGexHeatmap', settings.ticker, settings.size, settings.horizonDays],
    queryFn: () =>
      fetchDealerGexHeatmap({
        symbol: settings.ticker,
        size: settings.size,
        horizonDays: settings.horizonDays,
      }),
    staleTime: HEATMAP_STALE_MS,
    refetchInterval: HEATMAP_STALE_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  useEffect(() => {
    const svg = svgRef.current;
    const canvas = canvasRef.current;
    const frame = data && settings.greek === 'gamma' && data.terrain
      ? clockFrame(data, data.terrain)
      : null;
    if (!svg || !canvas || !data || (isEmptyHeatmap(data) && !frame)) {
      if (svg) svg.replaceChildren();
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    const surface = frame
      ? frame.field
      : customerGamma(settings.greek === 'charm' ? data.charm : data.z);
    drawHeatmap(
      canvas,
      svg,
      frame ? frame.view : data,
      box.width,
      box.height,
      `gex-heatmap-clip-${windowId}`,
      surface,
      frame ? frame.clock : null
    );
  }, [data, box.width, box.height, windowId, settings.greek]);

  const status = heatmapStatusCode(error);
  const showCharm = settings.greek === 'charm';
  const frame = data && !showCharm && data.terrain ? clockFrame(data, data.terrain) : null;
  const empty = isEmptyHeatmap(data) && !frame;
  const shownDate = frame ? (data?.terrain?.date ?? '') : (data?.date ?? '');
  const shownSpot = frame ? (lastPrint(frame.clock.path) ?? data?.spot ?? 0) : (data?.spot ?? 0);
  const charmMissing = showCharm && (data?.charm.length ?? 0) === 0;
  const dealerNet = showCharm ? (data?.stats.net_charm ?? 0) : (data?.stats.net_gex ?? 0);
  const net = -dealerNet;
  const peakDate = showCharm ? data?.stats.charm_peak_date : data?.stats.peak_abs_date;
  const peakStrike = showCharm ? data?.stats.charm_peak_strike : data?.stats.peak_abs_strike;

  const onPointerMove = (event: MouseEvent<HTMLDivElement>): void => {
    if (!data || empty || charmMissing) {
      setTip(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const surface = frame
      ? frame.field
      : customerGamma(showCharm ? data.charm : data.z);
    const hit = hoverCell(
      event.clientX - rect.left,
      event.clientY - rect.top,
      box.width,
      box.height,
      frame ? frame.view.strikes : data.strikes,
      frame ? frame.view.dates : data.dates,
      surface
    );
    if (!hit) {
      setTip(null);
      return;
    }
    setTip({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      date: hit.date,
      price: hit.price,
      value: hit.value,
    });
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      <WidgetHeader title="Gamma Heatmap" badge="beta" />
      <WidgetToolbar
        onClose={onRemove}
        leftControls={
          <div className="flex items-center gap-1">
            <form onSubmit={handleTickerSubmit}>
              <input
                type="text"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onFocus={() => {
                  if (isTickerLinked) return;
                  setTickerInput('');
                }}
                placeholder="Ticker"
                readOnly={isTickerLinked}
                title={
                  isTickerLinked
                    ? 'Linked — set the ticker from another linked widget or unlink to edit'
                    : undefined
                }
                className={`w-20 px-2 py-0.5 text-xs border rounded text-accent-yellow font-semibold tracking-wide placeholder:font-normal placeholder:tracking-normal placeholder-gray-500 focus:outline-none ${
                  isTickerLinked
                    ? 'bg-dark-surface border-accent-yellow ring-1 ring-accent-yellow/40 cursor-default select-none'
                    : 'bg-dark-surface border-dark-border focus:border-accent-yellow'
                }`}
              />
            </form>
            <TickerLinkButton
              windowId={windowId}
              currentTicker={settings.ticker}
              onTickerChange={handleTickerSelect}
            />
          </div>
        }
        centerControls={
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-dark-border rounded overflow-hidden">
              <SizeButton
                label="OI"
                title="Morning open interest plus today's inferred volume"
                active={settings.size === 'oi'}
                onClick={() => setSettings((prev) => ({ ...prev, size: 'oi' }))}
              />
              <SizeButton
                label="Vol Inf"
                title="Signed customer flow. Gamma draws the 0DTE cash session."
                active={settings.size === 'vol_inf'}
                onClick={() => setSettings((prev) => ({ ...prev, size: 'vol_inf' }))}
              />
            </div>
            <div className="flex items-center border border-dark-border rounded overflow-hidden">
              <SizeButton
                label="Gamma"
                active={settings.greek === 'gamma'}
                onClick={() => setSettings((prev) => ({ ...prev, greek: 'gamma' }))}
              />
              <SizeButton
                label="Charm"
                active={settings.greek === 'charm'}
                onClick={() => setSettings((prev) => ({ ...prev, greek: 'charm' }))}
              />
            </div>
            <label
              className="flex items-center gap-1 text-[11px] text-gray-500"
              title="Trading sessions. Expirations past the right edge stay in the book."
            >
              Horizon
              <select
                value={settings.horizonDays}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    horizonDays: parseHorizonDays(Number(e.target.value)),
                  }))
                }
                className="text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-0.5 text-gray-300 font-numeric focus:outline-none focus:border-accent-yellow"
              >
                {HEATMAP_HORIZON_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      />

      <div className="flex items-center gap-4 px-3 py-1.5 border-b border-dark-border text-[11px] text-gray-400 min-h-[28px] overflow-hidden">
        {data && !empty && (
          <>
            <span>
              {frame ? '0DTE' : 'EOD'}{' '}
              <span className="font-numeric text-white">{formatMd(shownDate) || '—'}</span>
            </span>
            {frame?.clock.asof && (
              <span>
                Book <span className="font-numeric text-white">{frame.clock.asof} ET</span>
              </span>
            )}
            <span>
              Spot{' '}
              <span className="font-numeric text-accent-yellow">
                {shownSpot > 0 ? shownSpot.toFixed(2) : '—'}
              </span>
            </span>
            {!frame && (
            <span>
              Customer{' '}
              <span
                className={`font-numeric ${
                  net > 0 ? 'text-bull-blue' : net < 0 ? 'text-bear-red' : 'text-white'
                }`}
              >
                {formatDealerGex(net)}
              </span>
            </span>
            )}
            {!frame && peakDate && peakStrike != null && (
              <span>
                {showCharm ? 'Peak |charm|' : 'Peak |Γ|'}{' '}
                <span className="font-numeric text-white">{formatMd(peakDate)}</span> @{' '}
                <span className="font-numeric text-white">{peakStrike}</span>
              </span>
            )}
            {data.earnings && (
              <span>
                Earnings{' '}
                <span className="font-numeric text-accent-yellow">
                  {formatMd(data.earnings.date)}
                  {data.earnings.time ? ` ${data.earnings.time.toUpperCase()}` : ''}
                </span>
              </span>
            )}
          </>
        )}
        {isFetching && !isLoading && (
          <span className="ml-auto text-gray-500">updating</span>
        )}
      </div>

      <div
        ref={wellRef}
        className="flex-1 relative min-h-0 bg-dark-surface"
        onMouseMove={onPointerMove}
        onMouseLeave={() => setTip(null)}
      >
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
        <svg ref={svgRef} className="absolute inset-0 w-full h-full pointer-events-none" />
        {tip && (
          <div
            className="pointer-events-none absolute z-10 rounded border border-dark-border bg-dark-bg/95 px-2 py-1 text-[11px] text-gray-200 shadow"
            style={{
              left: tip.x + 12,
              top: tip.y + 12,
              transform: tip.x > box.width * 0.65 ? 'translateX(calc(-100% - 24px))' : undefined,
            }}
          >
            <div className="font-numeric text-gray-400">{formatMd(tip.date)}</div>
            <div className="font-numeric">{tip.price.toFixed(2)}</div>
            <div
              className={`font-numeric ${
                tip.value > 0 ? 'text-bull-blue' : tip.value < 0 ? 'text-bear-red' : 'text-white'
              }`}
            >
              {formatDealerGex(tip.value)}
              <span className="ml-1 text-gray-500">
                {showCharm ? 'charm' : frame ? 'ES/pt' : 'γ/1%'}
              </span>
            </div>
          </div>
        )}
        {isLoading && !data && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            Loading heatmap…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-bear-red text-sm px-4 text-center">
            {status === 403
              ? 'Not admitted for this feed.'
              : status === 401
                ? 'Session expired. Refresh and try again.'
                : status === 422
                  ? 'Heatmap rejected this request.'
                  : 'Could not load heatmap.'}
          </div>
        )}
        {!error && !isLoading && empty && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm px-4 text-center">
            No EOD book for {getOptionsDisplayTicker(settings.ticker)}.
          </div>
        )}
        {!error && !isLoading && !empty && charmMissing && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm px-4 text-center">
            No charm on this book.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-3 py-1 border-t border-dark-border text-[10px] text-gray-500">
        <span>
          {showCharm
            ? 'red = customers buy as time passes · blue = customers sell · dashed = 1σ and 2σ'
            : 'blue = customer long gamma · red = customer short · dashed = 1σ and 2σ'}
        </span>
        <span>
          {frame
            ? '0DTE · ES / point · inventory held from the open'
            : `${settings.size === 'oi' ? "OI + today's inferred volume · " : ''}${
                showCharm ? 'to next session · $' : 'remaining book · $γ / 1%'
              }`}
        </span>
      </div>
    </div>
  );
}

function SizeButton({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title?: string;
}): ReactElement {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
        active
          ? 'bg-accent-yellow text-hot-tag-text'
          : 'bg-transparent text-gray-500 hover:text-gray-300'
      }`}
    >
      {label}
    </button>
  );
}
